import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import "../assets/css/style.css";
import { FiEdit2, FiTrash2 } from "react-icons/fi";
import Modal from "../components/ui/Modal";
import {
  useGetLegalDocumentsQuery,
  useCreateLegalDocumentMutation,
  useUpdateLegalDocumentMutation,
  useDeleteLegalDocumentMutation,
} from "apis/legalDocuments";
import { FaPlus } from "react-icons/fa6";
import LegalDocumentEdit from "components/legalDocuments/LegalDocumentEdit";
import LegalDocumentView from "components/legalDocuments/LegalDocumentView";
import Table from "components/ui/Table";

const LegalDocuments = () => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const pageSize = 10;

  const {
    data,
    isPending: isDataPending,
    isError: isDataError,
    error: dataError,
  } = useGetLegalDocumentsQuery();

  const {
    mutate: createLegalDocument,
    isLoading: isCreateLoading,
  } = useCreateLegalDocumentMutation();

  const { mutate: updateLegalDocument } = useUpdateLegalDocumentMutation();

  const {
    mutate: deleteLegalDocument,
    isLoading: isDeleteLoading,
  } = useDeleteLegalDocumentMutation();

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "name",
        header: "Document Name",
        isFilter: true,
        minWidth: "250px",
      },
      {
        accessorKey: "createdAt",
        header: "Created Date",
        cell: (row) => (
          <span className="text-sm">
            {new Date(row.createdAt).toLocaleDateString()}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "updatedAt",
        header: "Last Updated",
        cell: (row) => (
          <span className="text-sm">
            {new Date(row.updatedAt).toLocaleDateString()}
          </span>
        ),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => (
          <div className="flex items-center space-x-4">
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsEditOpen(true);
              }}
              className="edit-icon"
              title="Edit Document"
            >
              <FiEdit2 fontSize={20} />
            </button>
            <button
              onClick={() => {
                setSelectedId(row._id);
                setIsDeleteOpen(true);
              }}
              className="delete-icon"
              title="Delete Document"
            >
              <FiTrash2 fontSize={20} />
            </button>
          </div>
        ),
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
        minWidth: "120px",
      },
    ],
    []
  );

  if (isDataPending) {
    return <PageLoader />;
  }

  if (isDataError) {
    return (
      <div className="container mx-auto">
        <div className="text-center py-8">
          <p className="text-red-600">
            Error loading legal documents: {dataError?.message}
          </p>
        </div>
      </div>
    );
  }

  const selectedLegalDocument = data?.find(
    (document) => document._id === selectedId
  );

  const handleEditSubmit = (formData) => {
    updateLegalDocument({ id: selectedId, formData });
    setIsEditOpen(false);
  };

  const handleCreateSubmit = (formData) => {
    createLegalDocument(formData);
    setIsAddOpen(false);
  };

  const handleDelete = () => {
    deleteLegalDocument(selectedId);
    setIsDeleteOpen(false);
  };

  const handleViewRowClick = (row) => {
    setSelectedId(row._id);
    setIsViewOpen(true);
  };

  const AddLegalDocumentButton = () => (
    <button
      className="black-button flex items-center"
      onClick={() => setIsAddOpen(true)}
      disabled={isCreateLoading}
    >
      <FaPlus size={16} className="mr-1" />
      Add Document
    </button>
  );

  return (
    <>
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Edit Legal Document"
        className="max-w-3xl"
      >
        <LegalDocumentEdit
          initialData={selectedLegalDocument}
          onSubmit={handleEditSubmit}
        />
      </Modal>

      <Modal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        title="Add Legal Document"
        className="max-w-3xl"
      >
        <LegalDocumentEdit initialData={{}} onSubmit={handleCreateSubmit} />
      </Modal>

      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        title="Delete Legal Document"
      >
        <div>
          Are you sure you want to delete this legal document?
          <div className="w-full flex gap-2 justify-end py-4">
            <button
              onClick={() => setIsDeleteOpen(false)}
              className="gray-button"
            >
              No
            </button>
            <button onClick={handleDelete} className="black-button" disabled={isDeleteLoading}>
              Yes
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        title="Legal Document Details"
        className="max-w-3xl"
      >
        <LegalDocumentView document={selectedLegalDocument} />
      </Modal>

      <div className="container mx-auto">
        <Table
          title="Legal Documents"
          columns={columns}
          data={data || []}
          pageSize={pageSize}
          ButtonCmp={AddLegalDocumentButton}
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default LegalDocuments;
